import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { ControlValueAccessor } from '@angular/forms';
import { OnChangeFn, OnTouchedFn, provideCva } from '../cva.utils';
import { filterSelectOptions, SelectOption } from '../select-option.model';
import {
  computeDropdownPanelPosition,
  estimateDropdownContentHeight,
  findDropdownBoundaryRect,
} from '../dropdown-panel.utils';

export type ComboboxAppearance = 'outline' | 'underline';

@Component({
  selector: 'app-combobox',
  standalone: true,
  imports: [NgStyle],
  templateUrl: './combobox.component.html',
  styleUrls: ['./combobox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideCva(ComboboxComponent)],
  host: {
    '[class.app-combobox-open]': 'isOpen()',
    '[class.app-combobox-disabled]': 'disabled()',
    '[class.app-combobox-has-value]': 'hasValue()',
    '[class.app-combobox-error]': 'hasError()',
    '[class.app-combobox-underline]': 'appearance() === "underline"',
  },
})
export class ComboboxComponent implements ControlValueAccessor, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly placeholder = input<string>('Selecione...');
  readonly searchPlaceholder = input<string>('Buscar...');
  readonly options = input<SelectOption[]>([]);
  readonly appearance = input<ComboboxAppearance>('outline');
  readonly helperText = input<string>('');
  readonly errorMessage = input<string>('');
  readonly required = input<boolean>(false);
  readonly filterLocally = input<boolean>(true);

  readonly searchChange = output<string>();

  readonly value = signal<unknown>(null);
  readonly disabled = signal(false);
  readonly isOpen = signal(false);
  readonly touched = signal(false);
  readonly searchQuery = signal('');
  readonly opensAbove = signal(false);
  readonly panelStyles = signal<Record<string, string>>({});

  readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  readonly triggerRef = viewChild<ElementRef<HTMLElement>>('triggerRef');

  readonly hasValue = computed(
    () => this.value() !== null && this.value() !== undefined && this.value() !== '',
  );

  readonly hasError = computed(() => !!this.errorMessage());

  readonly displayMessage = computed(
    () => this.errorMessage() || this.helperText(),
  );

  readonly selectedLabel = computed(() => {
    const current = this.value();
    if (current === null || current === undefined || current === '') {
      return '';
    }
    return this.options().find((option) => option.value === current)?.label ?? '';
  });

  readonly filteredOptions = computed(() => {
    if (!this.filterLocally()) {
      return this.options();
    }
    return filterSelectOptions(this.options(), this.searchQuery());
  });

  readonly inputDisplayValue = computed(() => {
    if (this.isOpen()) {
      return this.searchQuery();
    }
    return this.selectedLabel();
  });

  private onChange: OnChangeFn = () => {};
  private onTouched: OnTouchedFn = () => {};

  private readonly onClickOutside = (event: MouseEvent) => {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.close();
    }
  };

  private readonly onReposition = () => {
    if (this.isOpen()) {
      this.updatePanelPosition();
    }
  };

  ngOnInit(): void {
    document.addEventListener('click', this.onClickOutside, true);
    window.addEventListener('scroll', this.onReposition, true);
    window.addEventListener('resize', this.onReposition);

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('click', this.onClickOutside, true);
      window.removeEventListener('scroll', this.onReposition, true);
      window.removeEventListener('resize', this.onReposition);
    });
  }

  writeValue(value: unknown): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: OnChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  open(): void {
    if (this.disabled()) {
      return;
    }
    this.isOpen.set(true);
    this.searchQuery.set(this.hasValue() ? this.selectedLabel() : '');
    queueMicrotask(() => {
      this.inputRef()?.nativeElement.focus();
      this.updatePanelPosition();
    });
  }

  close(): void {
    if (!this.isOpen()) {
      return;
    }
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.panelStyles.set({});
    this.opensAbove.set(false);
    if (!this.touched()) {
      this.touched.set(true);
      this.onTouched();
    }
  }

  onInputFocus(): void {
    this.open();
  }

  onSearchInput(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery.set(query);
    this.searchChange.emit(query);

    if (this.hasValue() && query !== this.selectedLabel()) {
      this.value.set(null);
      this.onChange(null);
    }

    if (!this.isOpen()) {
      this.isOpen.set(true);
    }

    queueMicrotask(() => this.updatePanelPosition());
  }

  select(option: SelectOption): void {
    this.value.set(option.value);
    this.onChange(option.value);
    this.close();
  }

  clearSelection(event: MouseEvent): void {
    event.stopPropagation();
    this.value.set(null);
    this.onChange(null);
    this.searchQuery.set('');
  }

  private updatePanelPosition(): void {
    const trigger = this.triggerRef()?.nativeElement;
    if (!trigger) {
      return;
    }

    const menu = trigger.querySelector('.combobox-menu') as HTMLElement | null;
    const contentHeight =
      menu?.scrollHeight ||
      estimateDropdownContentHeight(this.filteredOptions().length);

    const { opensAbove, styles } = computeDropdownPanelPosition(
      trigger.getBoundingClientRect(),
      {
        contentHeight,
        boundary: findDropdownBoundaryRect(trigger),
      },
    );
    this.opensAbove.set(opensAbove);
    this.panelStyles.set(styles);
    this.cdr.markForCheck();
  }
}
